import { createContext, useState, useContext, ReactNode, useCallback, useMemo } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { ThemeConfig, ThemeDirection, ThemeMode } from '../entities';
import { getTheme } from '../utils/getTheme';
import { getThemeMode } from '../utils/getThemeMode';

type ThemeContextType = {
  themeMode: ThemeMode;
  isLightTheme: boolean;
  changeTheme: (themeMode: ThemeMode) => void;
  direction: ThemeDirection;
  changeDirection: (direction: ThemeDirection) => void;
};

const ThemeContext = createContext<ThemeContextType>({
  themeMode: ThemeMode.LIGHT,
  isLightTheme: true,
  changeTheme: () => {},
  direction: ThemeDirection.LTR,
  changeDirection: () => {},
});

type ThemeProviderProps = {
  children: ReactNode;
  themeConfig?: ThemeConfig | null;
};

export const ThemeProvider = ({ children, themeConfig = null }: ThemeProviderProps) => {
  // CMS `palette.mode` drives the mode; the override exists only for the dev toggle
  const [themeModeOverride, setThemeModeOverride] = useState<ThemeMode | null>(null);
  const [direction, setDirection] = useState(ThemeDirection.LTR);

  const themeMode = themeModeOverride ?? getThemeMode(themeConfig);

  const changeTheme = useCallback((newTheme: ThemeMode) => {
    setThemeModeOverride(newTheme);
    // TODO:: will be removed after testing
    localStorage.setItem('theme', newTheme);
  }, []);

  const changeDirection = useCallback(
    (newDirection: ThemeDirection) => {
      setDirection(newDirection);
      document.documentElement.setAttribute('dir', newDirection);
    },
    [setDirection],
  );

  const theme = useMemo(
    () => getTheme(themeMode, direction, themeConfig),
    [themeMode, direction, themeConfig],
  );

  return (
    <ThemeContext.Provider
      value={{
        themeMode,
        changeTheme,
        isLightTheme: themeMode === ThemeMode.LIGHT,
        direction,
        changeDirection,
      }}
    >
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => useContext(ThemeContext);
